/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */
import React, { Component, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import imagesloaded from 'imagesloaded';
import Flickity from 'flickity';

export type FlickityOptions = Omit<Flickity.Options, 'initialIndex'> & { initialIndex?: number };

// Runtime members of Flickity missing from @types/flickity
interface FlickityInstance extends Flickity {
  options: FlickityOptions;
  activate(): void;
  deactivate(): void;
}

const setPointerEvents = (slide: ChildNode, value: string) => {
  if (slide instanceof HTMLElement) {
    slide.style.pointerEvents = value;
  }
};

interface FlickityWrapperProps {
  children?: ReactNode[];
  className?: string;
  disableImagesLoaded?: boolean;
  elementType?: string;
  flickityRef?: (flkty: FlickityInstance) => void;
  options?: FlickityOptions;
  reloadOnUpdate?: boolean;
  static?: boolean;
}

interface FlickityWrapperState {
  flickityReady: boolean;
}

export class FlickityWrapper extends Component<FlickityWrapperProps, FlickityWrapperState> {
  static defaultProps = {
    className: '',
    disableImagesLoaded: false,
    elementType: 'div',
    options: {},
    reloadOnUpdate: false,
    static: false,
  };

  state: FlickityWrapperState = {
    flickityReady: false,
  };

  carousel: HTMLElement | null = null;
  flkty: FlickityInstance | null = null;
  _isMounted = true;

  componentDidUpdate(prevProps: FlickityWrapperProps, prevState: FlickityWrapperState) {
    const {
      children,
      options: { draggable, initialIndex } = {},
      reloadOnUpdate,
    } = this.props;
    const { flickityReady } = this.state;
    if (!this.flkty) {
      return;
    }
    if (reloadOnUpdate || (!prevState.flickityReady && flickityReady)) {
      this.flkty.deactivate();
      this.flkty.selectedIndex = initialIndex || 0;
      this.flkty.options.draggable =
        draggable === undefined
          ? children
          ? children.length > 1
          : false
          : draggable;
      this.flkty.activate();
    } else {
      this.flkty.reloadCells();
    }
  }

  componentDidMount() {
    const { disableImagesLoaded, flickityRef, options } = this.props;
    const carousel = this.carousel;
    if (!carousel) {
      return;
    }
    // Why: @types/flickity omits the runtime options/activate/deactivate members used in componentDidUpdate
    const flkty = new Flickity(carousel, {
      ...options,
      on: {
        dragStart: function (this: Flickity) {
          this.slider.childNodes.forEach((slide) => setPointerEvents(slide, 'none'));
        },
        dragEnd: function (this: Flickity) {
          this.slider.childNodes.forEach((slide) => setPointerEvents(slide, 'all'));
        },
      },
    }) as FlickityInstance;
    this.flkty = flkty;

    const setFlickityToReady = () => {
      if (this._isMounted) {
        this.setState({ flickityReady: true });
      }
    }

    if (disableImagesLoaded) {
      setFlickityToReady();
    } else {
      imagesloaded(carousel, setFlickityToReady);
    }

    if (flickityRef) {
      flickityRef(flkty);
    }
  }

  componentWillUnmount() {
      this._isMounted = false;
  }

  renderPortal() {
    if (!this.carousel) return null;
    const mountNode = this.carousel.querySelector('.flickity-slider');
    if (mountNode) return createPortal(this.props.children, mountNode);
  }

  render() {
    const { elementType = 'div' } = this.props;
    return React.createElement(
      elementType,
      {
        className: this.props.className,
        ref: (c: HTMLElement | null) => {
          this.carousel = c;
        },
      },
      this.props.static ? this.props.children : this.renderPortal()
    );
  }
}
